import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  ["custom_scent", "Custom Scents"],
  ["experience", "Experiences"],
  ["favors", "Custom Favors"],
  ["contact", "Messages"],
  ["orders", "Orders"],
];

const FIELD_LABELS = {
  name: "Name", email: "Email", phone: "Phone", candle_size: "Candle Size", scent_family: "Scent Family",
  fragrance_notes: "Fragrance Notes", quantity: "Quantity", occasion: "Occasion", additional: "Additional Requests",
  event_date: "Event Date", event_location: "Location", event_type: "Event Type", guests: "Guests",
  preferred_time: "Preferred Time", details: "Details", subject: "Subject", message: "Message", interests: "Interested In",
};

const Admin = () => {
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("custom_scent");

  useEffect(() => {
    axios.get(`${API}/admin/inquiries`).then((r) => setInquiries(r.data)).catch(() => {});
    axios.get(`${API}/admin/orders`).then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => inquiries.filter((i) => i.type === tab), [inquiries, tab]);

  return (
    <div data-testid="admin-page" className="bg-ivory min-h-[70vh]">
      <section className="border-b border-line py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <Reveal>
            <p className="eyebrow">Merari Studio</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight">Submissions Inbox</h1>
            <p className="mt-4 max-w-lg text-sm text-stone2">Every custom scent request, experience booking, favor inquiry, and message — gathered in one place.</p>
          </Reveal>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 py-12">
        <div className="flex flex-wrap gap-2 border-b border-line pb-6" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`px-5 py-2.5 text-[10px] font-medium uppercase tracking-[0.22em] border transition-all duration-300 ${tab === key ? "border-ink bg-ink text-ivory" : "border-line bg-white text-stone2 hover:border-ink hover:text-ink"}`}>
              {label} {key !== "orders" && <span className="ml-1 opacity-60">({inquiries.filter((i) => i.type === key).length})</span>}
              {key === "orders" && <span className="ml-1 opacity-60">({orders.length})</span>}
            </button>
          ))}
        </div>
        <div className="mt-8 space-y-4 pb-16">
          {tab === "orders" ? (
            orders.length === 0 ? <p className="py-12 text-center font-serif text-xl font-light italic text-stone2" data-testid="admin-empty">No orders yet.</p> :
            orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.id.slice(0, 8)}`} className="border border-line bg-white p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="font-mono text-xs tracking-[0.2em]">ORDER {o.id.slice(0, 8).toUpperCase()}</p>
                  <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] border ${o.payment_status === "paid" ? "border-ink bg-ink text-ivory" : "border-line text-stone2"}`}>{o.payment_status}</span>
                </div>
                <ul className="mt-4 space-y-1 text-sm text-stone2">
                  {o.items.map((i) => <li key={i.slug}>{i.name} · {i.size} × {i.quantity} — ${(i.unit_price * i.quantity).toFixed(2)}</li>)}
                </ul>
                <p className="mt-4 font-serif text-xl font-light">Total ${o.amount.toFixed(2)}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone2">{new Date(o.created_at).toLocaleString()}</p>
              </div>
            ))
          ) : (
            filtered.length === 0 ? <p className="py-12 text-center font-serif text-xl font-light italic text-stone2" data-testid="admin-empty">Nothing here yet — submissions will appear as they arrive.</p> :
            filtered.map((inq) => (
              <div key={inq.id} data-testid={`admin-inquiry-${inq.id.slice(0, 8)}`} className="border border-line bg-white p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="font-serif text-xl font-light">{inq.data.name || "—"}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2">{new Date(inq.created_at).toLocaleString()}</p>
                </div>
                <dl className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2 text-sm">
                  {Object.entries(inq.data).filter(([k]) => k !== "name").map(([k, v]) => (
                    <div key={k} className="flex gap-3">
                      <dt className="w-32 shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-stone2 pt-0.5">{FIELD_LABELS[k] || k}</dt>
                      <dd className="text-ink">{Array.isArray(v) ? v.join(", ") : String(v || "—")}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;
